import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X } from 'lucide-react';
import adminMaterialStyleService from '../../services/api/adminMaterialStyleService';
import { ImageStyleCategory, ImageStyleCategoryCreateRequest, ImageStyleCategoryUpdateRequest } from '../../services/api/adminImageStyleService';
import {
  TaskyBadge,
  TaskyButton,
  TaskyErrorState,
  TaskyFormGroup,
  TaskyInput,
  TaskySpinner,
  TaskySwitch,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  TaskyTextarea,
} from '../tasky';

interface CategoryFormData {
  name: string;
  slug: string;
  description: string;
  sort_order: number;
  is_active: boolean;
}

const MaterialStyleCategoryList: React.FC = () => {
  const [categories, setCategories] = useState<ImageStyleCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ImageStyleCategory | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    slug: '',
    description: '',
    sort_order: 0,
    is_active: true,
  });

  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminMaterialStyleService.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch material style categories:', err);
      setError('素材カテゴリの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      sort_order: categories.length > 0 ? Math.max(...categories.map(c => c.sort_order)) + 1 : 1,
      is_active: true,
    });
    setIsModalOpen(true);
  };

  const handleEditCategory = (category: ImageStyleCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      sort_order: category.sort_order,
      is_active: category.is_active,
    });
    setIsModalOpen(true);
  };

  const handleSubmitCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (editingCategory) {
        const updateData: ImageStyleCategoryUpdateRequest = {
          name: formData.name,
          slug: formData.slug || undefined,
          description: formData.description || undefined,
          sort_order: formData.sort_order,
          is_active: formData.is_active,
        };
        await adminMaterialStyleService.updateCategory(editingCategory.id, updateData);
      } else {
        const createData: ImageStyleCategoryCreateRequest = {
          name: formData.name,
          slug: formData.slug || undefined,
          description: formData.description || undefined,
          sort_order: formData.sort_order,
          is_active: formData.is_active,
        };
        await adminMaterialStyleService.createCategory(createData);
      }
      setIsModalOpen(false);
      setEditingCategory(null);
      await fetchCategories();
    } catch (err) {
      console.error('素材カテゴリの保存に失敗しました:', err);
      setError('素材カテゴリの保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleCategory = async (categoryId: number) => {
    setIsLoading(true);
    try {
      await adminMaterialStyleService.toggleCategory(categoryId);
      await fetchCategories();
    } catch (err) {
      console.error('素材カテゴリの状態変更に失敗しました:', err);
      setError('素材カテゴリの状態変更に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    setIsLoading(true);
    try {
      await adminMaterialStyleService.deleteCategory(categoryId);
      setDeleteConfirmId(null);
      await fetchCategories();
    } catch (err: any) {
      console.error('素材カテゴリの削除に失敗しました:', err);
      const errorMessage = err.response?.data?.message || '素材カテゴリの削除に失敗しました';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  const sortedCategories = [...categories].sort((a, b) => {
    if (a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order;
    }
    return a.name.localeCompare(b.name);
  });

  if (isLoading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <TaskySpinner size="lg" aria-label="読み込み中" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-dark">素材スタイルカテゴリ一覧</h2>
          <TaskyBadge variant="warm">{categories.length}件</TaskyBadge>
        </div>
        <TaskyButton
          size="sm"
          onClick={handleCreateCategory}
          disabled={isLoading}
        >
          <Plus />
          新規カテゴリ追加
        </TaskyButton>
      </div>

      {error && (
        <TaskyErrorState
          className="mb-4"
          description={error}
          action={
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-gray-400 transition-colors hover:text-gray-600"
              aria-label="エラーを閉じる"
            >
              <X className="h-4 w-4" />
            </button>
          }
        />
      )}

      <TaskyTable>
        <TaskyTableHead>
          <tr>
            <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>カテゴリ名</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>スラッグ</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>説明</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>並び順</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>スタイル数</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>操作</TaskyTableHeaderCell>
          </tr>
        </TaskyTableHead>
        <TaskyTableBody>
          {sortedCategories.length === 0 ? (
            <TaskyTableRow hoverable={false}>
              <TaskyTableCell colSpan={8} align="center" className="py-8 text-gray-500">
                素材スタイルカテゴリが登録されていません
              </TaskyTableCell>
            </TaskyTableRow>
          ) : (
            sortedCategories.map((category) => (
              <TaskyTableRow key={category.id}>
                <TaskyTableCell className="text-gray-500">{category.id}</TaskyTableCell>
                <TaskyTableCell className="font-medium">{category.name}</TaskyTableCell>
                <TaskyTableCell>
                  <code className="rounded-tasky-xs bg-warm-surface px-1.5 py-0.5 text-xs text-dark">
                    {category.slug}
                  </code>
                </TaskyTableCell>
                <TaskyTableCell className="text-gray-600">
                  {category.description || '-'}
                </TaskyTableCell>
                <TaskyTableCell className="text-gray-500">{category.sort_order}</TaskyTableCell>
                <TaskyTableCell>
                  <TaskyBadge
                    className={category.is_active
                      ? 'bg-success/10 text-success'
                      : 'bg-gray-100 text-gray-500'}
                  >
                    {category.is_active ? '有効' : '無効'}
                  </TaskyBadge>
                </TaskyTableCell>
                <TaskyTableCell>
                  <TaskyBadge variant="warm">
                    {category.styles_count || 0}
                  </TaskyBadge>
                </TaskyTableCell>
                <TaskyTableCell>
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <button
                      type="button"
                      className="text-sm font-medium text-primary transition-colors hover:text-primary-hover disabled:cursor-not-allowed disabled:text-gray-300"
                      onClick={() => handleEditCategory(category)}
                      disabled={isLoading}
                    >
                      編集
                    </button>
                    <span className="text-gray-300">|</span>
                    <TaskySwitch
                      checked={category.is_active}
                      onChange={() => handleToggleCategory(category.id)}
                      disabled={isLoading}
                      aria-label={category.is_active ? '無効にする' : '有効にする'}
                      title={category.is_active ? '無効にする' : '有効にする'}
                    />
                    <span className="text-gray-300">|</span>
                    {deleteConfirmId === category.id ? (
                      <>
                        <button
                          type="button"
                          className="text-sm font-semibold text-destructive transition-colors hover:text-destructive/80 disabled:cursor-not-allowed disabled:text-gray-300"
                          onClick={() => handleDeleteCategory(category.id)}
                          disabled={isLoading}
                        >
                          削除実行
                        </button>
                        <button
                          type="button"
                          className="text-sm font-medium text-gray-500 transition-colors hover:text-dark disabled:cursor-not-allowed disabled:text-gray-300"
                          onClick={() => setDeleteConfirmId(null)}
                          disabled={isLoading}
                        >
                          キャンセル
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="text-sm font-medium text-destructive transition-colors hover:text-destructive/80 disabled:cursor-not-allowed disabled:text-gray-300"
                        onClick={() => setDeleteConfirmId(category.id)}
                        disabled={isLoading || (category.styles_count || 0) > 0}
                        title={
                          (category.styles_count || 0) > 0
                            ? 'このカテゴリは使用中のため削除できません'
                            : '削除'
                        }
                      >
                        削除
                      </button>
                    )}
                  </div>
                </TaskyTableCell>
              </TaskyTableRow>
            ))
          )}
        </TaskyTableBody>
      </TaskyTable>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => e.target === e.currentTarget && handleCloseModal()}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-tasky-lg bg-white shadow-card">
            <div className="flex shrink-0 items-center justify-between border-b border-warm-border px-6 py-4">
              <h5 className="text-lg font-semibold text-dark">
                {editingCategory ? 'カテゴリ編集' : '新規カテゴリ作成'}
              </h5>
              <button
                type="button"
                className="text-gray-400 transition-colors hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleCloseModal}
                disabled={isLoading}
                aria-label="閉じる"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitCategory} className="flex min-h-0 flex-col">
              <div className="space-y-4 overflow-y-auto px-6 py-4">
                <TaskyFormGroup label="カテゴリ名" required htmlFor="material-cat-name">
                  <TaskyInput
                    type="text"
                    id="material-cat-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={isLoading}
                  />
                </TaskyFormGroup>

                <TaskyFormGroup
                  label="スラッグ（英数字）"
                  htmlFor="material-cat-slug"
                  helper="空欄の場合はカテゴリ名から自動生成されます"
                >
                  <TaskyInput
                    type="text"
                    id="material-cat-slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    disabled={isLoading}
                    placeholder="空欄の場合は自動生成"
                  />
                </TaskyFormGroup>

                <TaskyFormGroup label="説明" htmlFor="material-cat-desc">
                  <TaskyTextarea
                    id="material-cat-desc"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    disabled={isLoading}
                    rows={3}
                  />
                </TaskyFormGroup>

                <TaskyFormGroup label="並び順" htmlFor="material-cat-order">
                  <TaskyInput
                    type="number"
                    id="material-cat-order"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    disabled={isLoading}
                    min={0}
                  />
                </TaskyFormGroup>

                <TaskySwitch
                  label="有効"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  disabled={isLoading}
                />
              </div>
              <div className="flex shrink-0 justify-end gap-3 border-t border-warm-border bg-warm-bg px-6 py-4">
                <TaskyButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCloseModal}
                  disabled={isLoading}
                >
                  キャンセル
                </TaskyButton>
                <TaskyButton
                  type="submit"
                  size="sm"
                  disabled={isLoading || !formData.name}
                >
                  {isLoading ? '保存中...' : '保存'}
                </TaskyButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialStyleCategoryList;
